/**
 * Planet Renderer - 3D-like Planet Visuals
 * 
 * Creates visually rich planets with:
 * - Radial gradients for 3D sphere effect
 * - Highlight and shadow
 * - Atmospheric glow
 * - Ring effects (for some planets)
 * - Surface detail patterns
 */

import Phaser from 'phaser';

export interface Planet3DConfig {
  x: number;
  y: number;
  radius: number;
  color: number;
  hasRing?: boolean;
  hasAtmosphere?: boolean;
}

export interface Planet3DSprites {
  container: Phaser.GameObjects.Container;
  glow: Phaser.GameObjects.Arc;
  shadow: Phaser.GameObjects.Arc;
  body: Phaser.GameObjects.Arc;
  highlight: Phaser.GameObjects.Arc;
  ring?: Phaser.GameObjects.Graphics;
  atmosphere?: Phaser.GameObjects.Arc;
}

/**
 * Create a 3D-looking planet using layered graphics
 */
export function createPlanet3D(
  scene: Phaser.Scene,
  config: Planet3DConfig
): Planet3DSprites {
  const { x, y, radius, color, hasRing, hasAtmosphere } = config;
  
  // Create container to hold all planet parts
  const container = scene.add.container(x, y);
  
  // 1. Outer glow (atmosphere effect)
  const glowRadius = radius + 20;
  const glow = scene.add.circle(0, 0, glowRadius, color, 0.15);
  container.add(glow);
  
  // 2. Atmosphere glow (optional, softer outer ring)
  let atmosphere: Phaser.GameObjects.Arc | undefined;
  if (hasAtmosphere) {
    atmosphere = scene.add.circle(0, 0, radius + 10, color, 0.25);
    atmosphere.setStrokeStyle(4, color, 0.4);
    container.add(atmosphere);
  }
  
  // 3. Main planet body - dark base color
  const darkColor = darkenColor(color, 0.6);
  const body = scene.add.circle(0, 0, radius, darkColor);
  body.setStrokeStyle(2, 0xffffff, 0.2);
  container.add(body);
  
  // 4. Shadow overlay (bottom right)
  const shadowOffsetX = radius * 0.15;
  const shadowOffsetY = radius * 0.15;
  const shadow = scene.add.circle(shadowOffsetX, shadowOffsetY, radius * 0.95, 0x000000, 0.4);
  container.add(shadow);
  
  // 5. Highlight (top left - simulates light source)
  const highlightOffsetX = -radius * 0.3;
  const highlightOffsetY = -radius * 0.3;
  const highlight = scene.add.circle(highlightOffsetX, highlightOffsetY, radius * 0.6, color, 0.5);
  container.add(highlight);
  
  // 6. Smaller bright highlight (specular)
  const specularOffsetX = -radius * 0.35;
  const specularOffsetY = -radius * 0.35;
  const specular = scene.add.circle(specularOffsetX, specularOffsetY, radius * 0.2, 0xffffff, 0.5);
  container.add(specular);
  
  // 7. Ring (optional - for gas giants)
  let ring: Phaser.GameObjects.Graphics | undefined;
  if (hasRing) {
    ring = scene.add.graphics();
    const ringColor = lightenColor(color, 0.3);
    
    // Draw elliptical ring
    ring.lineStyle(3, ringColor, 0.6);
    ring.strokeEllipse(0, 0, radius * 2.5, radius * 0.6);
    ring.lineStyle(1, 0xffffff, 0.3);
    ring.strokeEllipse(0, 0, radius * 2.2, radius * 0.5);
    
    container.add(ring);
  }
  
  // Set depth for proper layering
  container.setDepth(10);
  
  return {
    container,
    glow,
    shadow,
    body,
    highlight,
    ring,
    atmosphere
  };
}

/**
 * Update planet position
 */
export function updatePlanet3DPosition(
  sprites: Planet3DSprites,
  x: number,
  y: number
): void {
  sprites.container.setPosition(x, y);
}

/**
 * Set planet visibility
 */
export function setPlanet3DVisible(
  sprites: Planet3DSprites,
  visible: boolean
): void {
  sprites.container.setVisible(visible);
}

/**
 * Animate planet (rotation, pulse, etc.)
 */
export function animatePlanet3D(
  scene: Phaser.Scene,
  sprites: Planet3DSprites
): void {
  // Subtle pulse on the glow
  scene.tweens.add({
    targets: sprites.glow,
    scaleX: 1.05,
    scaleY: 1.05,
    alpha: 0.2,
    duration: 2000,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut'
  });
  
  // Slow rotation of highlight to simulate rotation
  scene.tweens.add({
    targets: sprites.highlight,
    x: { from: sprites.highlight.x - 2, to: sprites.highlight.x + 2 },
    duration: 4000,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut'
  });
}

/**
 * Create player with 3D effect
 */
export function createPlayer3D(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  color: number
): Phaser.GameObjects.Container {
  const container = scene.add.container(x, y);
  
  // Trail glow
  const glow = scene.add.circle(0, 0, radius + 8, color, 0.3);
  container.add(glow);
  
  // Main body
  const body = scene.add.circle(0, 0, radius, color);
  body.setStrokeStyle(2, 0xffffff, 0.6);
  container.add(body);
  
  // Highlight
  const highlight = scene.add.circle(-radius * 0.3, -radius * 0.3, radius * 0.4, 0xffffff, 0.6);
  container.add(highlight);
  
  // Pulse animation
  scene.tweens.add({
    targets: glow,
    scaleX: 1.2,
    scaleY: 1.2,
    alpha: 0.15,
    duration: 1000,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut'
  });
  
  container.setDepth(50);
  
  return container;
}

// ============================================================
// COLOR UTILITIES
// ============================================================

/**
 * Darken a hex color by a factor (0-1)
 */
function darkenColor(hex: number, factor: number): number {
  const r = Math.floor(((hex >> 16) & 0xff) * factor);
  const g = Math.floor(((hex >> 8) & 0xff) * factor);
  const b = Math.floor((hex & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

/**
 * Lighten a hex color by a factor (0-1 where 1 = white)
 */
function lightenColor(hex: number, factor: number): number {
  const r = Math.min(255, Math.floor(((hex >> 16) & 0xff) + (255 - ((hex >> 16) & 0xff)) * factor));
  const g = Math.min(255, Math.floor(((hex >> 8) & 0xff) + (255 - ((hex >> 8) & 0xff)) * factor));
  const b = Math.min(255, Math.floor((hex & 0xff) + (255 - (hex & 0xff)) * factor));
  return (r << 16) | (g << 8) | b;
}

export default {
  createPlanet3D,
  updatePlanet3DPosition,
  setPlanet3DVisible,
  animatePlanet3D,
  createPlayer3D
};
